import clsx from "clsx";
import { Loader2 } from "lucide-react";
import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from "react";

type BaseProps = {
  size?: "sm" | "md" | "lg";
  color?: "primary" | "secondary" | "secondary-inverse";
  isLoading?: boolean;
  children: React.ReactNode;
};

type ButtonV2Props =
  | (BaseProps & ButtonHTMLAttributes<HTMLButtonElement> & { href?: never })
  | (BaseProps & AnchorHTMLAttributes<HTMLAnchorElement> & { href: string });

const sizeClasses = {
  sm: "px-2 py-1 text-sm",
  md: "px-4 py-2 text-base",
  lg: "px-6 py-3 text-lg",
} as const;

const colorClasses = {
  primary: "bg-turquoise text-white hover:bg-turquoise/90 active:bg-turquoise/80",
  secondary:
    "bg-transparent text-white border border-white hover:bg-white hover:text-black active:bg-white/90 active:text-black",
  "secondary-inverse": "bg-[#E1E1E1] text-black hover:bg-[#D1D1D1] active:bg-[#C1C1C1]",
} as const;

const ButtonV2 = ({
  children,
  size = "md",
  color = "primary",
  isLoading = false,
  className,
  href,
  ...rest
}: ButtonV2Props) => {
  const loadingClasses = isLoading ? "cursor-not-allowed opacity-70" : "";

  const commonClasses = clsx(
    "rounded-full font-medium transition-all duration-200 flex items-center justify-center gap-2",
    sizeClasses[size],
    colorClasses[color],
    loadingClasses,
    className,
  );

  const content = isLoading ? (
    <>
      <Loader2 className="h-4 w-4 animate-spin" />
      {children}
    </>
  ) : (
    children
  );

  if (href) {
    const isPdf = href.toLowerCase().endsWith(".pdf");
    const anchorProps = rest as AnchorHTMLAttributes<HTMLAnchorElement>;

    return (
      <a
        className={commonClasses}
        href={href}
        target={isPdf ? "_blank" : anchorProps.target}
        rel={isPdf ? "noopener noreferrer" : anchorProps.rel}
        {...anchorProps}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      className={commonClasses}
      type={(rest as ButtonHTMLAttributes<HTMLButtonElement>).type || "button"}
      disabled={isLoading}
      {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {content}
    </button>
  );
};

export default ButtonV2;
